import { useCallback } from 'react';

import { urlParser } from '@hh.ru/browser-api-utils';
import { usePush } from '@hh.ru/redux-spa-middleware';
import { RProgressApi } from '@hh.ru/rprogress';

import scrollToElement from 'Utils/ScrollToElement';
import { Step, StepName } from 'src/components/VacancyCreate/contexts/StepsContext/types';

const useHandlers = ({
    steps,
    currentStepIndex,
    setCurrentStepIndex,
    setActiveStepIndex,
    recreateSteps,
    onOpenNextStep,
}: {
    steps: Step[];
    currentStepIndex: number;
    activeStepIndex: number;
    setCurrentStepIndex: (stepIndex: number) => void;
    setActiveStepIndex: (stepIndex: number) => void;
    recreateSteps: (standaloneStepName: StepName | null) => void;
    onOpenNextStep?: (currentStepIndex: number, nextStepIndex: number) => void;
}) => {
    const push = usePush();

    const pushToStep = useCallback(
        ({
            standaloneStepName = null,
            stepIndexToScroll = null,
        }: {
            standaloneStepName?: StepName | null;
            stepIndexToScroll?: number | null;
        } = {}) => {
            RProgressApi.start();

            setTimeout(() => {
                const url = urlParser(`${location.pathname}${location.search}`);
                url.params.stepName = standaloneStepName;
                url.params.selectPublicationType = null;
                url.params.step = stepIndexToScroll;

                recreateSteps(standaloneStepName);

                push(url.href, { cancelScrollCorrection: true }, { cancelFetchingData: true });

                RProgressApi.complete();
            }, 500);
        },
        [push, recreateSteps]
    );

    const scrollToStep = useCallback(
        (stepIndex: number) => {
            const scrollTarget = document.querySelector(`[data-anchor=${steps[stepIndex].name}]`);

            if (scrollTarget) {
                scrollToElement(scrollTarget, {
                    centered: false,
                    topOffset: 0,
                    animationEndCallback: () => {
                        setActiveStepIndex(stepIndex);
                    },
                });
            }
        },
        [setActiveStepIndex, steps]
    );

    const openNextStep = useCallback(() => {
        const nextStepIndex = Math.min(currentStepIndex + 1, steps.length - 1);

        onOpenNextStep?.(currentStepIndex, nextStepIndex);

        if (steps[nextStepIndex].isStandalonable) {
            pushToStep({ standaloneStepName: steps[nextStepIndex].name });
        } else {
            setCurrentStepIndex(nextStepIndex);
        }
    }, [currentStepIndex, onOpenNextStep, pushToStep, setCurrentStepIndex, steps]);

    const openStep = useCallback(
        (stepIndex: number, withScroll: boolean | undefined = true) => {
            if (steps[stepIndex].isStandalonable) {
                pushToStep({ standaloneStepName: steps[stepIndex].name });
            } else if (steps[currentStepIndex].isStandalonable) {
                pushToStep({ standaloneStepName: null, stepIndexToScroll: withScroll ? stepIndex : null });
            } else if (currentStepIndex >= stepIndex) {
                scrollToStep(stepIndex);
            }
        },
        [currentStepIndex, pushToStep, scrollToStep, steps]
    );

    return { scrollToStep, openNextStep, openStep };
};

export { useHandlers };
